import { useCallback } from 'react';
import {
	useLocation,
	useNavigate as useRouterNavigate,
	type NavigateOptions,
	type To,
} from 'react-router-dom';

type NavigateTarget = number | To;

const splitTarget = (target: string) => {
	const [targetWithSearch, hash = ''] = target.split('#', 2);
	const queryIndex = targetWithSearch.indexOf('?');

	return {
		pathname: queryIndex >= 0 ? targetWithSearch.slice(0, queryIndex) : targetWithSearch,
		search: queryIndex >= 0 ? targetWithSearch.slice(queryIndex + 1) : '',
		hash,
	};
};

export default function useNavigate() {
	const navigate = useRouterNavigate();
	const { pathname: currentPathname, search: currentSearch } = useLocation();

	return useCallback(
		(target: NavigateTarget, options?: NavigateOptions) => {
			if (typeof target === 'number') {
				navigate(target);
				return;
			}

			const targetData = typeof target === 'string' ? splitTarget(target) : target;
			const currentSearchParams = new URLSearchParams(currentSearch);
			const targetSearchParams = new URLSearchParams(targetData.search || '');

			currentSearchParams.forEach((value, key) => {
				if (!targetSearchParams.has(key)) {
					targetSearchParams.set(key, value);
				}
			});

			const search = targetSearchParams.toString();
			const hash = targetData.hash ? `#${targetData.hash.replace(/^#/, '')}` : '';

			navigate(`${targetData.pathname || currentPathname}${search ? `?${search}` : ''}${hash}`, options);
		},
		[currentPathname, currentSearch, navigate]
	);
}
